import { ReactNode, InputHTMLAttributes, TextareaHTMLAttributes, SelectHTMLAttributes } from 'react';

// ── Label ─────────────────────────────────────────────────────────────────────

interface FieldLabelProps {
    htmlFor?: string;
    required?: boolean;
    children: ReactNode;
}

/**
 * DS: Uppercase bold field label.
 */
export function FieldLabel({ htmlFor, required, children }: FieldLabelProps) {
    return (
        <label htmlFor={htmlFor} className="block text-xs font-bold uppercase mb-2">
            {children}{required && ' *'}
        </label>
    );
}

// ── Input ─────────────────────────────────────────────────────────────────────

type BrutalInputProps = InputHTMLAttributes<HTMLInputElement>;

/**
 * DS: Standard brutalist text input — thick black border, orange focus ring.
 */
export function BrutalInput({ className, ...props }: BrutalInputProps) {
    return (
        <input
            className={`w-full px-4 py-3 border-2 border-black focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white ${className ?? ''}`}
            {...props}
        />
    );
}

// ── Search Input ──────────────────────────────────────────────────────────────

type SearchInputProps = InputHTMLAttributes<HTMLInputElement>;

/**
 * DS: Monospace search input — used in list page headers.
 */
export function SearchInput({ className, ...props }: SearchInputProps) {
    return (
        <input
            type="search"
            className={`w-full md:w-64 px-3 py-2 border-2 border-black text-sm font-mono bg-white focus:outline-none focus:border-orange-500 ${className ?? ''}`}
            {...props}
        />
    );
}

// ── Textarea ──────────────────────────────────────────────────────────────────

type BrutalTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

/**
 * DS: Brutalist textarea — matches BrutalInput styling.
 */
export function BrutalTextarea({ className, ...props }: BrutalTextareaProps) {
    return (
        <textarea
            className={`w-full px-4 py-3 border-2 border-black focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white resize-y ${className ?? ''}`}
            {...props}
        />
    );
}

// ── Select ────────────────────────────────────────────────────────────────────

type BrutalSelectProps = SelectHTMLAttributes<HTMLSelectElement>;

/**
 * DS: Brutalist select dropdown — matches the psychographics select in PersonaDetail.
 */
export function BrutalSelect({ className, children, ...props }: BrutalSelectProps) {
    return (
        <select
            className={`w-full px-4 py-3 border-2 border-black focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white ${className ?? ''}`}
            {...props}
        >
            {children}
        </select>
    );
}

// ── FieldGroup ────────────────────────────────────────────────────────────────

interface FieldGroupProps {
    label: string;
    htmlFor?: string;
    required?: boolean;
    children: ReactNode;
}

/**
 * DS: Combines FieldLabel + any input in a standard vertical stack.
 *
 * @example
 * <FieldGroup label="Name" htmlFor="name" required>
 *   <BrutalInput id="name" value={name} onChange={...} />
 * </FieldGroup>
 */
export function FieldGroup({ label, htmlFor, required, children }: FieldGroupProps) {
    return (
        <div>
            <FieldLabel htmlFor={htmlFor} required={required}>{label}</FieldLabel>
            {children}
        </div>
    );
}
